import type {KeyValue} from '@jetbrains/teamcity-ui/dist/utils'
import {normalize, schema} from 'normalizr'

import type {
  TestOccurrenceInvocations,
  TestOccurrencesCounts,
} from '../components/packages/Tests/Tests.types'
import type {
  Agent,
  Build,
  BuildProblem,
  BuildType,
  Change as RestChange,
  Mute,
  Pipeline,
  ProblemOccurrence,
  Project,
  TestOccurrence,
  TestProblem,
} from '../services/rest'
import type {
  AgentId,
  AgentPreview,
  BranchWithBuilds,
  BuildId,
  BuildTypeId,
  ChangeId,
  CloudImagesHash,
  CompatibleAgent,
  EntityArchivedSubprojectsIds,
  EntityDescription,
  EntityProjectsIdsList,
  InexactEntityParameters,
  Investigation,
  NormalizedAgent,
  NormalizedAgentPreview,
  NormalizedBranchWithBuilds,
  NormalizedBuild,
  NormalizedChange,
  NormalizedPipelineRun,
  NormalizedPipelineRunJob,
  NormalizedProject,
  NormalizedRevision,
  NormalizedVcsRootInstance,
  PipelineId,
  ProblemOccurrenceId,
  ProjectId,
  RequestOptionsParams,
  Revision,
  SidebarProject,
  SnapshotDependenciesIDList,
  StatusRequest,
  TestId,
  TestOccurrenceId,
  UniqueRevisionId,
  VcsLabel,
  VcsRoot,
  VcsRootId,
  VcsRootInstanceId,
  WebLinks,
} from '../types'
import {getBuildTypeStatusRequest, getUniqueRevisionId, stringifyId} from '../types'
import {stringifyBranch} from '../utils/branchNames'
import {getEmptyHash} from '../utils/empty'
import memoryCache from '../utils/memoryCache'

const getLinks = (entity: WebLinks): WebLinks => ({
  links: entity.links,
})

const getDescription = (entity: EntityDescription): EntityDescription => ({
  description: entity.description,
})

const getArchivedSubprojectsIds = (entity: EntityArchivedSubprojectsIds): EntityProjectsIdsList =>
  entity.projects?.project ?? []

const getParameters = (entity: InexactEntityParameters): InexactEntityParameters => ({
  parameters: entity.parameters,
})

const buildType = new schema.Entity('buildTypes')

const projectPipelines = new schema.Entity('pipelines')
const projectPipelineRun = new schema.Entity(
  'pipelineRuns',
  {},
  {
    idAttribute: ({build}) => build?.id,
  },
)
projectPipelineRun.define({
  pipeline: projectPipelines,
})
const projectPipelineRunJob = new schema.Entity(
  'pipelineRunJobs',
  {},
  {
    idAttribute: ({build}) => build?.id,
  },
)
projectPipelineRunJob.define({
  pipelineRun: projectPipelineRun,
})
const vcsRoots = new schema.Entity('vcsRoots')
const vcsRootInstances = new schema.Entity('vcsRootInstances', {
  'vcs-root': vcsRoots,
})
const changes = new schema.Entity('changes', {
  vcsRootInstance: vcsRootInstances,
})

const modificationsOfChanges = new schema.Entity('modificationsOfChanges', {
  mergedInfo: {
    changes: {
      change: [changes],
    },
  },
})
const revisions = new schema.Entity(
  'revisions',
  {
    'vcs-root-instance': vcsRootInstances,
  },
  {
    idAttribute: ({version, 'vcs-root-instance': vcsRootInstance}: Revision) =>
      getUniqueRevisionId(vcsRootInstance.id, version),
  },
)
const vcsLabels = new schema.Entity(
  'vcsLabels',
  {},
  {
    idAttribute: (_, build) => stringifyId(build?.id),
    processStrategy: labels => (labels ? Object.keys(labels).map(key => labels[key]) : []),
  },
)

const processAgentStrategy = ({environment, ...rest}: Agent) => ({...environment, ...rest})

const build = new schema.Entity(
  'builds',
  {
    buildType,
    pipelines: projectPipelines,
    pipelineRunJob: projectPipelineRunJob,
    pipelineRun: projectPipelineRun,
    versionedSettingsRevision: revisions,
    triggered: {
      buildType,
    },
    changes: {
      change: [changes],
    },
    revisions: {
      revision: [revisions],
    },
    vcsLabels,
  },
  {
    processStrategy: ({agent, ...rest}) => ({
      ...rest,
      ...(agent
        ? {
            agent: processAgentStrategy(agent),
          }
        : {}),
    }),
  },
)
build.define({
  related: {
    builds: {
      build: [build],
    },
  },
  delayedByBuild: build,
  matrixConfiguration: {
    dependencies: {
      dependency: [{build}],
    },
  },
  artifactDependencyChanges: {
    buildChange: [{nextBuild: build}],
  },
  firstBuildWithSameChanges: build,
  anchorBuild: build,
})

const buildArtifacts = new schema.Entity('buildArtifacts')
const compatibleAgents = new schema.Entity('compatibleAgents')
const agentCloudImage = new schema.Entity(
  'cloudImage',
  {},
  {
    idAttribute: ({agent}) => agent?.typeId,
    processStrategy: ({image: {name, profile}, agent}) => ({
      name,
      agentTypeId: agent?.typeId,
      agentPoolId: agent?.pool?.id,
      agentIds: getEmptyHash(),
      profile: {
        id: profile?.id,
        name: profile?.name,
        projectId: profile?.project?.id,
      },
    }),
  },
)
const agent = new schema.Entity(
  'agents',
  {
    build,
    cloudInstance: agentCloudImage,
  },
  {
    processStrategy: data =>
      processAgentStrategy({...data, cloudInstanceId: data.cloudInstance?.id}),
  },
)

const processAgentPreviewStrategy = ({environment, ...rest}: AgentPreview) => ({
  ...rest,
  osType: environment?.osType,
  ...('build' in rest
    ? {
        build: rest.build?.id,
      }
    : {}),
})

const agentPreview = new schema.Entity(
  'agentPreviews',
  {},
  {
    processStrategy: processAgentPreviewStrategy,
  },
)
const testProblems = new schema.Entity(
  'testProblems',
  {},
  {
    idAttribute: ({test: {id}}) => id,
  },
)
const testProblemTestOccurrences = new schema.Entity(
  'testProblemTestOccurrences',
  {},
  {
    processStrategy: (entity: TestOccurrence): TestOccurrence => {
      const {mute, test, nextFixed, invocations, metadata, ...restProperties} = entity
      return {
        ...restProperties,
      }
    },
  },
)
const testProblemTestOccurrenceBuilds = new schema.Entity('testProblemTestOccurrenceBuilds', {
  build,
  firstFailed: {
    build,
  },
})
const testProblemsFailingBuildTypesCount = new schema.Entity(
  'testProblemsFailingBuildTypesCount',
  {},
  {
    idAttribute: ({test: {id}}) => id,
    processStrategy: (entity: TestProblem): number | null | undefined =>
      entity.failingBuildTypes?.count,
  },
)
const testProblemsFailingBuildTypes = new schema.Entity(
  'testProblemsFailingBuildTypes',
  {},
  {
    idAttribute: ({test: {id}}) => id,
    processStrategy: (entity: TestProblem): BuildType[] | null | undefined =>
      entity.failingBuildTypes?.buildType,
  },
)
const testProblemsMutes = new schema.Entity(
  'testProblemsMutes',
  {},
  {
    idAttribute: ({test: {id}}) => id,
    processStrategy: (entity: TestProblem): Mute[] | null | undefined => entity.mutes?.mute,
  },
)
const testProblemsInvestigations = new schema.Entity(
  'testProblemsInvestigations',
  {},
  {
    idAttribute: ({test: {id}}) => id,
    processStrategy: (entity: TestProblem): Investigation[] | null | undefined =>
      entity.investigations?.investigation as Investigation[],
  },
)
const buildProblems = new schema.Entity(
  'buildProblems',
  {},
  {
    idAttribute: ({problemOccurrence: {id}}) => id,
  },
)
const buildProblemsMutes = new schema.Entity(
  'buildProblemsMutes',
  {},
  {
    idAttribute: ({problemOccurrence: {id}}) => id,
    processStrategy: (entity: BuildProblem): Mute[] | null | undefined => entity.mutes?.mute,
  },
)
const buildProblemsInvestigations = new schema.Entity(
  'buildProblemsInvestigations',
  {},
  {
    idAttribute: ({problemOccurrence: {id}}) => id,
    processStrategy: (entity: BuildProblem): Investigation[] | null | undefined =>
      entity.investigations?.investigation as Investigation[],
  },
)
const problemOccurrences = new schema.Entity('problemOccurrences')
const testOccurrences = new schema.Entity(
  'testOccurrences',
  {},
  {
    processStrategy: (entity: TestOccurrence): TestOccurrence => {
      const {firstFailed, mute, test, ...restTestOccurrencesProps} = entity
      const {mutes, investigations, ...testTestProps} = test ?? {}
      return {...restTestOccurrencesProps, test: testTestProps}
    },
  },
)
const testOccurrencesNewFailure = new schema.Entity(
  'testOccurrencesNewFailure',
  {},
  {
    processStrategy: (entity: TestOccurrence): boolean | null | undefined => entity.newFailure,
  },
)
const testOccurrencesInvocations = new schema.Entity('testOccurrencesInvocations', {
  invocations: {
    testOccurrence: [testOccurrences],
  },
})
const testOccurrencesMetadataCount = new schema.Entity(
  'testOccurrencesMetadataCount',
  {},
  {
    processStrategy: (entity: TestOccurrence): number | null | undefined => entity.metadata?.count,
  },
)
const testOccurrencesFirstFailed = new schema.Entity(
  'testOccurrencesFirstFailed',
  {},
  {
    processStrategy: (entity: TestOccurrence): TestOccurrence | null | undefined =>
      entity.firstFailed,
  },
)
const testOccurrencesNextFixed = new schema.Entity(
  'testOccurrencesNextFixed',
  {},
  {
    processStrategy: (entity: TestOccurrence): TestOccurrence | null | undefined =>
      entity.nextFixed,
  },
)
const testOccurrencesRunOrder = new schema.Entity(
  'testOccurrencesRunOrder',
  {},
  {
    processStrategy: (entity: TestOccurrence): string | null | undefined => entity.runOrder,
  },
)
const testOccurrencesInvestigations = new schema.Entity(
  'testOccurrencesInvestigations',
  {},
  {
    processStrategy: (entity: TestOccurrence) => entity.test?.investigations?.investigation,
  },
)
const testOccurrencesCurrentlyMutes = new schema.Entity(
  'testOccurrencesCurrentlyMutes',
  {},
  {
    processStrategy: (entity: TestOccurrence): ReadonlyArray<Mute> | null | undefined =>
      entity.test?.mutes?.mute,
  },
)
const testOccurrencesInvocationsCounters = new schema.Entity(
  'testOccurrencesInvocationsCounters',
  {},
  {
    processStrategy: (entity: TestOccurrence): TestOccurrencesCounts | null | undefined =>
      entity.invocations?.testCounters,
  },
)
const testOccurrencesMute = new schema.Entity(
  'testOccurrencesMute',
  {},
  {
    processStrategy: (entity: TestOccurrence): Mute | null | undefined => entity.mute,
  },
)
const testOccurrencesBuild = new schema.Entity(
  'testOccurrences',
  {
    build,
  },
  {},
)
export type StatusKey = string
export const getStatusKey = (request: StatusRequest | null | undefined): StatusKey =>
  request
    ? [
        request.type,
        request.id,
        request.branch ? stringifyBranch(request.branch) : '',
        request.branch?.default === true ? 'default' : '',
      ]
        .filter(Boolean)
        .join(':')
    : ''
export const stringifyKey = (key: StatusKey): string => key
const branch = new schema.Entity(
  'branches',
  {
    builds: {
      build: [build],
    },
  },
  {
    idAttribute: item => getStatusKey(getBuildTypeStatusRequest(item.buildTypeId, item)),
    mergeStrategy: (branchA, branchB) => ({
      ...branchA,
      builds: {
        build: [...branchA.builds.build, ...branchB.builds.build],
      },
    }),
  },
)
const buildTypeLinks = new schema.Entity(
  'buildTypeLinks',
  {},
  {
    processStrategy: getLinks,
  },
)
const buildTypeDescription = new schema.Entity(
  'buildTypeDescription',
  {},
  {
    processStrategy: getDescription,
  },
)

const buildTypeParameters = new schema.Entity(
  'buildTypeParameters',
  {},
  {
    processStrategy: getParameters,
  },
)
const projectLinks = new schema.Entity(
  'projectLinks',
  {},
  {
    processStrategy: getLinks,
  },
)
const projectDescription = new schema.Entity(
  'projectDescription',
  {},
  {
    processStrategy: getDescription,
  },
)
const projectArchivedSubprojectsIds = new schema.Entity(
  'projectArchivedSubprojectsIds',
  {},
  {
    processStrategy: getArchivedSubprojectsIds,
  },
)
const projectParameters = new schema.Entity(
  'projectParameters',
  {},
  {
    processStrategy: getParameters,
  },
)
const ancestorProject = new schema.Entity('projects')
const projectWithBuildTypes = new schema.Entity('projects', {
  buildTypes: {
    buildType: [buildType],
  },
  pipelines: {
    pipeline: [projectPipelines],
  },
  ancestorProjects: {
    project: [ancestorProject],
  },
})
const sidebarProject = new schema.Entity('sidebarProjects')
sidebarProject.define({ancestorProjects: {project: [sidebarProject]}})
const projectWithBuildTypesLink = new schema.Entity('projects', {
  buildTypes: {
    buildType: [buildTypeLinks],
  },
  pipelines: {
    pipeline: [projectPipelines],
  },
})
const projectWithBuildTypesDescription = new schema.Entity('projects', {
  buildTypes: {
    buildType: [buildTypeDescription],
  },
  pipelines: {
    pipeline: [projectPipelines],
  },
})
const projectWithBuildTypesParameters = new schema.Entity('projects', {
  buildTypes: {
    buildType: [buildTypeParameters],
  },
  pipelines: {
    pipeline: [projectPipelines],
  },
})
const buildsWithBuildTypesLink = new schema.Entity('builds', {
  buildType: buildTypeLinks,
  pipelines: {
    pipeline: [projectPipelines],
  },
})
const agentWithBuildsAndBuildTypeLinks = new schema.Entity(
  'agents',
  {
    build: buildsWithBuildTypesLink,
  },
  {
    processStrategy: data =>
      processAgentStrategy({...data, cloudInstanceId: data.cloudInstance?.id}),
  },
)
export type Entities = {
  builds: KeyValue<BuildId, NormalizedBuild | null>
  buildsTestOccurrencesCount: KeyValue<BuildId, number | undefined>
  buildArtifacts: KeyValue<BuildId, NormalizedBuild | null>
  compatibleAgents: KeyValue<BuildId, CompatibleAgent | null>
  agents: KeyValue<AgentId, NormalizedAgent | null>
  agent: KeyValue<AgentId, NormalizedAgent | null | undefined>
  agentPreviews: KeyValue<AgentId, NormalizedAgentPreview | null | undefined>
  cloudImage: CloudImagesHash
  buildTypes: KeyValue<BuildTypeId, BuildType>
  pipelines: KeyValue<PipelineId, Pipeline>
  pipelineRuns: KeyValue<string, NormalizedPipelineRun>
  pipelineRunJobs: KeyValue<string, NormalizedPipelineRunJob>
  buildTypeParameters: KeyValue<BuildTypeId, InexactEntityParameters>
  buildTypeLinks: KeyValue<BuildTypeId, WebLinks>
  buildTypeDescription: KeyValue<BuildTypeId, EntityDescription>
  projects: KeyValue<ProjectId, NormalizedProject>
  sidebarProjects: KeyValue<ProjectId, SidebarProject>
  projectDescription: KeyValue<ProjectId, EntityDescription>
  projectArchivedSubprojectsIds?: KeyValue<ProjectId, EntityProjectsIdsList>
  projectParameters: KeyValue<ProjectId, InexactEntityParameters>
  projectLinks: KeyValue<ProjectId, WebLinks>
  branches?: KeyValue<StatusKey, NormalizedBranchWithBuilds>
  testOccurrences: KeyValue<TestOccurrenceId, TestOccurrence>
  multirunTestOccurrences: KeyValue<TestOccurrenceId, TestOccurrence>
  testOccurrencesInvocations?: KeyValue<TestOccurrenceId, TestOccurrenceInvocations>
  testOccurrencesFirstFailed: KeyValue<TestOccurrenceId, TestOccurrence | null | undefined>
  testOccurrencesNextFixed: KeyValue<TestOccurrenceId, TestOccurrence | null | undefined>
  testOccurrencesRunOrder: KeyValue<TestOccurrenceId, string | null | undefined>
  testOccurrencesNewFailure: KeyValue<TestOccurrenceId, boolean | null | undefined>
  testOccurrencesMetadataCount: KeyValue<TestOccurrenceId, number | null | undefined>
  testOccurrencesInvestigations: KeyValue<TestOccurrenceId, ReadonlyArray<Investigation>>
  testOccurrencesCurrentlyMutes: KeyValue<TestOccurrenceId, ReadonlyArray<Mute>>
  testOccurrencesMute: KeyValue<TestOccurrenceId, Mute | null | undefined>
  testOccurrencesInvocationsCounters: KeyValue<TestOccurrenceId, TestOccurrencesCounts>
  testProblems: KeyValue<TestId, TestProblem>
  testProblemsMutes: KeyValue<TestId, ReadonlyArray<Mute>>
  testProblemsInvestigations: KeyValue<TestId, ReadonlyArray<Investigation>>
  testProblemTestOccurrences: KeyValue<TestOccurrenceId, TestOccurrence>
  testProblemsFailingBuildTypesCount: KeyValue<TestId, number | null | undefined>
  testProblemsFailingBuildTypes: KeyValue<TestId, BuildType[] | null | undefined>
  buildProblems: KeyValue<ProblemOccurrenceId, BuildProblem>
  buildProblemsMutes: KeyValue<ProblemOccurrenceId, ReadonlyArray<Mute>>
  buildProblemsInvestigations: KeyValue<ProblemOccurrenceId, ReadonlyArray<Investigation>>
  problemOccurrences: KeyValue<ProblemOccurrenceId, ProblemOccurrence>
  changes: KeyValue<ChangeId, NormalizedChange>
  modificationsOfChanges: KeyValue<ChangeId, NormalizedChange>
  revisions: KeyValue<UniqueRevisionId, NormalizedRevision>
  vcsLabels: KeyValue<BuildId, ReadonlyArray<VcsLabel>>
  vcsRootInstances: KeyValue<VcsRootInstanceId, NormalizedVcsRootInstance>
  vcsRoots: KeyValue<VcsRootId, VcsRoot>
  snapshotDependencies: SnapshotDependenciesIDList
}
export type Normalized<T> = Readonly<{
  result: T
  entities: Partial<Entities>
}>
export type NormalizedSingleBuild = Normalized<BuildId>
export type NormalizedBuilds = Normalized<ReadonlyArray<BuildId>>
export type NormalizedAgents = Normalized<ReadonlyArray<AgentId>>
export type NormalizedSingleAgent = Normalized<AgentId>
export type NormalizedSingleBuildType = Normalized<BuildTypeId>
export type NormalizedBuildTypes = Normalized<ReadonlyArray<BuildTypeId>>
export type NormalizedSingleProject = Normalized<ProjectId>
export type NormalizedProjects = Normalized<ReadonlyArray<ProjectId>>
type NormalizedBuildTypeLinks = Normalized<BuildTypeId>
type NormalizedBuildTypeParameters = Normalized<BuildTypeId>
export type NormalizedBranches = Normalized<ReadonlyArray<StatusKey>>
export type NormalizedTestOccurrences = Normalized<ReadonlyArray<TestOccurrenceId>>
type NormalizedProblemOccurrences = Normalized<ReadonlyArray<ProblemOccurrenceId>>
export type NormalizedTestProblems = Normalized<ReadonlyArray<TestId>>
type NormalizedBuildProblems = Normalized<ReadonlyArray<ProblemOccurrenceId>>
export type NormalizedChanges = Normalized<ReadonlyArray<ChangeId>>
export const normalizeBuild: (response: Build) => NormalizedSingleBuild = response =>
  normalize(response, build)
export const normalizeBuilds = (response: ReadonlyArray<Build>): NormalizedBuilds =>
  normalize(response, [build])
export const normalizeBuildArtifacts = (response: ReadonlyArray<Build>): NormalizedBuilds =>
  normalize(response, [buildArtifacts])
export const normalizeCompatibleAgents = (response: ReadonlyArray<Build>): NormalizedBuilds =>
  normalize(response, [compatibleAgents])
export const normalizeAgents = (response: ReadonlyArray<Agent>): NormalizedAgents =>
  normalize(response, [agent])
export const normalizeAgentPreviews = (response: ReadonlyArray<Agent>): NormalizedAgents =>
  normalize(response, [agentPreview])
export const normalizeAgent = (response: Agent): NormalizedSingleAgent => normalize(response, agent)
export const normalizeBuildType: (response: BuildType) => NormalizedSingleBuildType = response =>
  normalize(response, buildType)
export const normalizeBuildTypes = (response: ReadonlyArray<BuildType>): NormalizedBuildTypes =>
  normalize(response, [buildType])
export const normalizeBuildTypeLinks: (
  response: BuildType,
) => NormalizedBuildTypeLinks = response => normalize(response, buildTypeLinks)
export const normalizeBuildTypeDescription: (
  response: BuildType,
) => NormalizedBuildTypeLinks = response => normalize(response, buildTypeDescription)
export const normalizeBuildTypeParameters: (
  response: BuildType,
) => NormalizedBuildTypeParameters = response => normalize(response, buildTypeParameters)

const normalizeProjects = (response: ReadonlyArray<Project>): NormalizedProjects =>
  normalize(response, [projectWithBuildTypes])

export const normalizeBuildTypeLinksFromBuilds = (
  response: ReadonlyArray<Build>,
): NormalizedBuilds => normalize(response, [buildsWithBuildTypesLink])
export const normalizeBuildTypeLinksFromAgentsAndBuilds = (
  response: ReadonlyArray<Agent>,
): NormalizedAgents => normalize(response, [agentWithBuildsAndBuildTypeLinks])

const normalizeBuildTypeLinksFromProjects = (
  response: ReadonlyArray<Project>,
): NormalizedProjects => normalize(response, [projectWithBuildTypesLink])

const normalizeBuildTypeDescriptionFromProjects = (
  response: ReadonlyArray<Project>,
): NormalizedProjects => normalize(response, [projectWithBuildTypesDescription])

const normalizeBuildTypeParametersFromProjects = (
  response: ReadonlyArray<Project>,
): NormalizedProjects => normalize(response, [projectWithBuildTypesParameters])

const normalizeProjectsArchivedSubprojectsIds = (
  response: ReadonlyArray<Project>,
): NormalizedSingleProject => normalize(response, [projectArchivedSubprojectsIds])

const normalizeProjectsLinks = (response: ReadonlyArray<Project>): NormalizedProjects =>
  normalize(response, [projectLinks])

const normalizeProjectsDescription = (response: ReadonlyArray<Project>): NormalizedProjects =>
  normalize(response, [projectDescription])

const normalizeProjectsParameters = (response: ReadonlyArray<Project>): NormalizedProjects =>
  normalize(response, [projectParameters])

export const normalizeBranches = (response: ReadonlyArray<BranchWithBuilds>): NormalizedBranches =>
  normalize(response, [branch])
export const normalizeTestOccurrences = (
  response: ReadonlyArray<TestOccurrence>,
): NormalizedTestOccurrences => normalize(response, [testOccurrences])
export const normalizeTestOccurrencesInvocations = (
  response: ReadonlyArray<TestOccurrence>,
): NormalizedTestOccurrences => normalize(response, [testOccurrencesInvocations])
export const normalizeTestOccurrencesNewFailure = (
  response: ReadonlyArray<TestOccurrence>,
): NormalizedTestOccurrences => normalize(response, [testOccurrencesNewFailure])
export const normalizeTestOccurrencesMetadataCount = (
  response: ReadonlyArray<TestOccurrence>,
): NormalizedTestOccurrences => normalize(response, [testOccurrencesMetadataCount])
export const normalizeTestOccurrencesFirstFailed = (
  response: ReadonlyArray<TestOccurrence>,
): NormalizedTestOccurrences => normalize(response, [testOccurrencesFirstFailed])
export const normalizeTestOccurrencesNextFixed = (
  response: ReadonlyArray<TestOccurrence>,
): NormalizedTestOccurrences => normalize(response, [testOccurrencesNextFixed])
export const normalizeTestOccurrencesRunOrder = (
  response: ReadonlyArray<TestOccurrence>,
): NormalizedTestOccurrences => normalize(response, [testOccurrencesRunOrder])
export const normalizeTestOccurrencesInvestigations = (
  response: ReadonlyArray<TestOccurrence>,
): NormalizedTestOccurrences => normalize(response, [testOccurrencesInvestigations])
export const normalizeTestOccurrencesCurrentlyMutes = (
  response: ReadonlyArray<TestOccurrence>,
): NormalizedTestOccurrences => normalize(response, [testOccurrencesCurrentlyMutes])
export const normalizeTestOccurrencesInvocationsCounters = (
  response: ReadonlyArray<TestOccurrence>,
): NormalizedTestOccurrences => normalize(response, [testOccurrencesInvocationsCounters])
export const normalizeTestOccurrencesMute = (
  response: ReadonlyArray<TestOccurrence>,
): NormalizedTestOccurrences => normalize(response, [testOccurrencesMute])
export const normalizeTestOccurrencesBuild = (
  response: ReadonlyArray<TestOccurrence>,
): NormalizedTestOccurrences => normalize(response, [testOccurrencesBuild])
export const normalizeProblemOccurrences = (
  response: ReadonlyArray<ProblemOccurrence>,
): NormalizedProblemOccurrences => normalize(response, [problemOccurrences])
export const normalizeTestProblems = (
  response: ReadonlyArray<TestProblem>,
): NormalizedTestProblems => normalize(response, [testProblems])
export const normalizeTestProblemTestOccurrences = (
  response: ReadonlyArray<TestOccurrence>,
): NormalizedTestOccurrences => normalize(response, [testProblemTestOccurrences])
export const normalizeTestProblemTestOccurrenceBuilds = (
  response: ReadonlyArray<TestOccurrence>,
): NormalizedTestOccurrences => normalize(response, [testProblemTestOccurrenceBuilds])
export const normalizeTestProblemsFailingBuildTypesCount = (
  response: ReadonlyArray<TestProblem>,
): NormalizedTestProblems => normalize(response, [testProblemsFailingBuildTypesCount])
export const normalizeTestProblemsFailingBuildTypes = (
  response: ReadonlyArray<TestProblem>,
): NormalizedTestProblems => normalize(response, [testProblemsFailingBuildTypes])
export const normalizeTestProblemsMutes = (
  response: ReadonlyArray<TestProblem>,
): NormalizedTestProblems => normalize(response, [testProblemsMutes])
export const normalizeTestProblemsInvestigations = (
  response: ReadonlyArray<TestProblem>,
): NormalizedTestProblems => normalize(response, [testProblemsInvestigations])
export const normalizeBuildProblems = (
  response: ReadonlyArray<BuildProblem>,
): NormalizedBuildProblems => normalize(response, [buildProblems])
export const normalizeBuildProblemsMutes = (
  response: ReadonlyArray<BuildProblem>,
): NormalizedBuildProblems => normalize(response, [buildProblemsMutes])
export const normalizeBuildProblemsInvestigations = (
  response: ReadonlyArray<BuildProblem>,
): NormalizedBuildProblems => normalize(response, [buildProblemsInvestigations])
export const normalizeBuildChanges = (response: ReadonlyArray<Build>): NormalizedBuilds =>
  normalize(response, [build])
export const normalizeChanges = (response: ReadonlyArray<RestChange>): NormalizedChanges =>
  normalize(response, [changes])
export const normalizeModificationsOfChange = (response: RestChange): Normalized<ChangeId> =>
  normalize(response, modificationsOfChanges)
export const normalizeProjectsData = (
  data: ReadonlyArray<Project>,
  options: RequestOptionsParams,
  resetCache = true,
): NormalizedProjects => {
  if (resetCache) {
    memoryCache.reset('fullPath')
  }
  const project = options.isSidebar ? normalize(data, [sidebarProject]) : normalizeProjects(data)
  let entities = {...project.entities}

  if (options.withDescription === true) {
    entities = {
      ...entities,
      ...normalizeProjectsDescription(data).entities,
      ...normalizeBuildTypeDescriptionFromProjects(data).entities,
    }
  }

  if (options.withLinks === true) {
    entities = {...entities, ...normalizeProjectsLinks(data).entities}
  }

  if (options.withParameters === true) {
    entities = {...entities, ...normalizeProjectsParameters(data).entities}
  }

  if (options.withBuildTypes === true && options.withLinks === true) {
    entities = {
      ...entities,
      ...normalizeProjectsLinks(data).entities,
      ...normalizeBuildTypeLinksFromProjects(data).entities,
    }
  }

  if (options.withBuildTypes === true && options.withParameters === true) {
    entities = {...entities, ...normalizeBuildTypeParametersFromProjects(data).entities}
  }

  if (options.withArchivedSubprojectsIds === true) {
    entities = {...entities, ...normalizeProjectsArchivedSubprojectsIds(data).entities}
  }

  // freeze to speedup immer finalize, see https://immerjs.github.io/immer/performance#pre-freeze-data
  return Object.freeze({
    result: project.result,
    entities,
  })
}
